import type { ScreenBase } from "@frui.ts/screens";
import { BusyWatcher, watchBusy } from "@frui.ts/screens";
import type { PropertyName } from "@frui.ts/helpers";
import { computed, observable } from "mobx";
import { Modules } from "config";
import TransferType from "entities/transferType";
import type { IBankExportDetail } from "entities/bankExport";
import { Action } from "entities/bankExport";
import type EventBus from "services/eventBus";
import type OptionsService from "services/optionsService";
import type LocalizationService from "services/localizationService";
import type AppRouter from "services/router";
import type SecurityService from "services/securityService";
import DetailViewModelWithChildren from "../../detailViewModelWithChildren";
import type BankExportDetailListViewModel from "./bankExportDetailListViewModel";
import { allowToDownload, allowToDownloadFinance } from "./rights";
import type NotificationService from "../../../services/notificationService";

export default abstract class BankExportDetailViewModel<
  TEntity extends IBankExportDetail = IBankExportDetail
> extends DetailViewModelWithChildren<TEntity, ScreenBase> {
  @observable show: boolean;
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  abstract download(item: TEntity): Promise<void>;

  protected constructor(
    protected itemId: string,
    protected bankExportDetailListViewModel: ReturnType<typeof BankExportDetailListViewModel.Factory>,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected optionsService: OptionsService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected notificationService: NotificationService
  ) {
    super();
    this.navigationName = "";
  }

  @watchBusy
  async onInitialize(): Promise<void> {
    this.handleOpen();
    await super.onInitialize();
    this.children.push(this.bankExportDetailListViewModel(this.createActionList()));
    await this.tryActivateChild(this.children[0]);
  }

  handleOpen() {
    this.show = true;
  }

  @computed
  get editLabel() {
    return this.translate("edit");
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService) && !allowToDownloadFinance(this.securityService)) {
      return false;
    }
    return true;
  }

  abstract backLink(): void;

  handleClose() {
    this.backLink();
    this.show = false;
  }

  async createActionList() {
    const item = await this.loadDetail();
    let actionList: Action[] = [];
    actionList = item.downloads.map(download => {
      const action = new Action();
      action.description = this.translate("actionDownload") + " " + item.name.toLowerCase();
      action.actionBy = download.downloadedBy;
      action.actionAt = download.downloadedAt;
      return action;
    });
    actionList.push({
      description: this.translate("actionCreate") + " " + item.name.toLowerCase(),
      actionBy: item.createdBy,
      actionAt: item.createdAt,
    });
    return actionList;
  }

  translateAttribute(attribute: PropertyName<TEntity>): string {
    return this.localizationService.translateAttribute("bankExport", attribute);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `bankExports.${code}`, size);
  }

  @computed
  get refundTypeOptions() {
    return this.optionsService.internalEnumOptions(TransferType);
  }
}
